// React
import { useEffect, useRef, useState } from "react"

// Store
import useAuthenticationStore from "src/store/authentication"

// Components
import MainCard from "src/components/common/main-card"
import Transitions from "src/components/common/transition"

// Router
import { useRouter } from "next/router"
import navList from "src/components/layout/drawer/nav-config"

// Mui
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme
} from "@mui/material"

// Icons
import { Home, LogoutCurve, User, UserSquare } from "iconsax-react"
import { useIsMobile } from "src/hooks/useIsMobile"
import auth from "../../../../../lib/auth"
import Pages from "src/constants/pages"

// --------------------------------------|| PROFILE - COMPONENTS ||-----------------------------------------
function UserMenuItem({ onClick, isSelected, Icon, label }) {
  return (
    <ListItem sx={{ p: 0 }}>
      <ListItemButton selected={isSelected} onClick={onClick}>
        <ListItemIcon sx={{ minWidth: "2rem" }}>
          <Icon size={18} variant='Bold' color='#0958d9' />
        </ListItemIcon>
        <ListItemText>
          <Typography variant='subtitle2'>{label}</Typography>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}
const Profile = () => {
  const isMobile = useIsMobile()
  // Use Theme
  const theme = useTheme()


  // Store
  const { doLogout } = useAuthenticationStore()

  // Router
  const router = useRouter()

  // States
  const [state, setState] = useState("error" | "loading" | "done")
  const [open, setOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(router.pathname)
  const [downloaded, setDownloaded] = useState(false)
  const [user, setUser] = useState()
  const [dialog, setDialog] = useState(false)

  // Menu Ref
  const anchorRef = useRef(null)

  // Toggle Menu
  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  // Handle Logout
  const handleLogout = () => {
    setState("loading")
    auth
      .logout(router)
      .then(() => {
        setState("done")
      })
      .catch(() => {
        setState("error")
      })
  }

  const handleListItemClick = (event, path) => {
    setSelectedItem(path)
    router.push(path)
    setOpen(false)
  }

  useEffect(() => {
    setUser(auth.user)
    setDownloaded(true)
  }, [])

  const renderUserInfo = () => {
    if (downloaded) {
      return (
        <>
          <ListItem sx={{ py: 0, px: 1 }}>
            <ListItemAvatar>
              <Avatar>
                <UserSquare />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant='caption' fontWeight={600}>
                  {user?.firstName ? `${user?.firstName} ${user?.lastName ?? ""}` : user?.nationalCode}
                </Typography>
              }
              secondary={<Typography variant='subtitle2'>{user?.mobileNumber}</Typography>}
            />
          </ListItem>

          <Divider sx={{ mt: 1, borderStyle: "dashed" }} />
        </>
      )
    }
  }

  return (
    <>
      <Box sx={{ flexShrink: 0 }}>
        <IconButton
          sx={{
            boxShadow: 3,
            fontSize: 16,
            fontWeight: 600,
            p: 1,
            color: theme.palette.text.primary,
            borderRadius: "4px",
            backgroundColor: open ? theme.palette.grey[400] : theme.palette.background.paper,
            "&:hover": {
              backgroundColor: theme.palette.grey[400]
            }
          }}
          onClick={handleToggle}
          ref={anchorRef}
        >
          <User size={18} />
        </IconButton>
        <Popper
          sx={{ zIndex: 2000 }}
          placement='bottom-end'
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          popperOptions={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 0]
                }
              }
            ]
          }}
        >
          {({ TransitionProps }) => (
            <Transitions type='fade' in={open} {...TransitionProps}>
              <Paper
                sx={{
                  borderRadius: "10px",
                  boxShadow: theme.shadows[2],
                  width: 290,
                  minWidth: 270,
                  maxWidth: 220,
                  [theme.breakpoints.down("md")]: {
                    maxWidth: 200
                  }
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Typography>
                    <MainCard sx={{ width: "100%", borderRadius: "8px", px: 0, pb: 0 }}>
                      {renderUserInfo()}

                      <List>
                        {isMobile && (
                          <>
                            <UserMenuItem
                              key={"home"}
                              isSelected={selectedItem === "/"}
                              onClick={e => handleListItemClick(e, "/")}
                              label={"صفحه نخست"}
                              Icon={Home}
                            />
                            {navList.map(({ title, path, icon }) => (
                              <UserMenuItem
                                key={path}
                                isSelected={selectedItem === path}
                                onClick={e => handleListItemClick(e, path)}
                                label={title}
                                Icon={icon}
                              />
                            ))}
                            <Divider m={1} />
                          </>
                        )}
                        <UserMenuItem
                          isSelected={selectedItem === Pages.Profile}
                          onClick={e => handleListItemClick(e, Pages.Profile)}
                          label='حساب کاربری'
                          Icon={User}
                        />
                        <UserMenuItem
                          onClick={e => {
                            setOpen(false)
                            setDialog(true)
                          }}
                          label='خروج از حساب'
                          Icon={LogoutCurve}
                        />
                      </List>
                    </MainCard>
                  </Typography>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      </Box>
      <Dialog open={dialog} maxWidth='xs' fullWidth onClose={() => setDialog(false)}>
        <DialogContent>
          <Typography variant='h5'>خروج از حساب</Typography>

          <Typography sx={{ my: 3 }}>برای خروج از حساب مطمئن هستید ؟</Typography>

          <Stack direction='row' justifyContent='end' spacing={1}>
            <Button
              disabled={state === "loading"}
              variant='outlined'
              onClick={() => setDialog(false)}
              sx={{ borderRadius: "8px" }}
            >
              لغو
            </Button>
            <Button
              disabled={state === "loading"}
              variant='contained'
              onClick={handleLogout}
              sx={{ borderRadius: "8px" }}
            >
              {state === "loading" ? <CircularProgress size={16} /> : "خروج"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Profile
